"use client"

import * as React from "react"
import { DayButton, DayPicker, getDefaultClassNames } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button, buttonVariants } from "@/components/ui/button"
import MaterialSymbolsNavigateBefore from "@/icons/component/MaterialSymbolsNavigateBefore"
import MaterialSymbolsNavigateNext from "@/icons/component/MaterialSymbolsNavigateNext"
import MaterialSymbolsExpandMore from "@/icons/component/MaterialSymbolsExpandMore"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  hideNavigation,
  formatters,
  components,
  locale,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"]
  locale?: string
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      hideNavigation={hideNavigation}
      className={cn(
        "bg-background group/calendar p-3 [--cell-size:2rem] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString(locale || undefined, { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn(
          "relative flex flex-col gap-4 md:flex-row",
          defaultClassNames.months
        ),
        month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-[--cell-size] w-[--cell-size] select-none p-0 aria-disabled:opacity-50 hover:bg-primary/10 hover:text-primary transition-all duration-200 ease-in-out bg-transparent border-0",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-[--cell-size] w-[--cell-size] select-none p-0 aria-disabled:opacity-50 hover:bg-primary/10 hover:text-primary transition-all duration-200 ease-in-out bg-transparent border-0",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex h-[--cell-size] w-full items-center justify-center px-[--cell-size]",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex h-[--cell-size] w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "relative rounded-md bg-white border border-border hover:border-primary/50 has-focus:border-2 has-focus:border-primary has-focus:outline-none transition-all duration-200 ease-in-out px-3 py-2 h-auto",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          "bg-white absolute inset-0 opacity-0 appearance-none rounded-md px-3 py-2 text-sm font-medium outline-none cursor-pointer transition-all duration-200 ease-in-out text-foreground",
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          "select-none font-medium text-foreground bg-white",
          captionLayout === "label"
            ? "text-sm"
            : "[&>svg]:text-action flex h-auto items-center gap-1 rounded-md px-2 py-1 text-sm [&>svg]:size-3.5",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "text-muted-foreground flex-1 select-none rounded-md text-[0.8rem] font-normal",
          defaultClassNames.weekday
        ),
        week: cn("mt-2 flex w-full", defaultClassNames.week),
        week_number_header: cn(
          "w-[--cell-size] select-none",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-muted-foreground select-none text-[0.8rem]",
          defaultClassNames.week_number
        ),
        day: cn(
          "group/day relative aspect-square h-full w-full select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md",
          defaultClassNames.day
        ),
        range_start: cn(
          "bg-accent rounded-l-md",
          defaultClassNames.range_start
        ),
        range_middle: cn("rounded-none", defaultClassNames.range_middle),
        range_end: cn("bg-accent rounded-r-md", defaultClassNames.range_end),
        today: cn(
          "bg-accent text-foreground rounded-md",
          defaultClassNames.today
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn(
          "text-muted-foreground opacity-50",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          )
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <MaterialSymbolsNavigateBefore
                className={cn("size-4", className)}
                {...props}
              />
            )
          }

          if (orientation === "right") {
            return (
              <MaterialSymbolsNavigateNext
                className={cn("size-4", className)}
                {...props}
              />
            )
          }

          return (
            <MaterialSymbolsExpandMore
              className={cn("size-4", className)}
              {...props}
            />
          )
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-[--cell-size] items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "relative flex aspect-square h-auto w-full min-w-[--cell-size] flex-col gap-1 font-normal leading-none transition-all duration-200 ease-in-out rounded-md",
        // Remove ALL default focus/ring/border/shadow styles
        "focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 focus:border-0 focus-visible:border-0 focus:shadow-none focus-visible:shadow-none",
        "aria-selected:ring-0 aria-selected:outline-none aria-selected:border-0 aria-selected:shadow-none",
        // Hover state
        "hover:bg-primary/10 hover:text-primary",
        // Single selection - FILLED rectangular background with primary purple and WHITE text
        "data-[selected-single=true]:!bg-primary data-[selected-single=true]:!text-white data-[selected-single=true]:font-medium data-[selected-single=true]:hover:!bg-primary/90",
        "data-[selected-single=true]:!border-0 data-[selected-single=true]:!shadow-none data-[selected-single=true]:!outline-none data-[selected-single=true]:!ring-0",
        "data-[selected-single=true]:hover:!text-white",
        // Range states - FILLED rectangular
        "data-[range-middle=true]:!bg-accent data-[range-middle=true]:!text-accent-foreground data-[range-middle=true]:rounded-none",
        "data-[range-start=true]:!bg-primary data-[range-start=true]:!text-primary-foreground data-[range-start=true]:font-medium data-[range-start=true]:rounded-md data-[range-start=true]:hover:!bg-primary/90",
        "data-[range-end=true]:!bg-primary data-[range-end=true]:!text-primary-foreground data-[range-end=true]:font-medium data-[range-end=true]:rounded-md data-[range-end=true]:hover:!bg-primary/90",
        // NO focus rings or outlines
        "focus-visible:!ring-0 focus-visible:!outline-none focus-visible:!border-0 focus-visible:!shadow-none",
        "group-data-[focused=true]/day:!ring-0 group-data-[focused=true]/day:!outline-none group-data-[focused=true]/day:!border-0 group-data-[focused=true]/day:!shadow-none",
        // Additional styles
        "[&>span]:text-xs [&>span]:opacity-70",
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
